import React, { useEffect, useState } from 'react';
import type { Pin, AccountAddress } from './mapPins/MapPinTypes';
import { PIN_TYPE } from './mapPins/MapPins';
import Map from './Map';
import GeocodeResource from 'App/resources/Geocode';
import { useTranslation } from 'Components/Helpers/translate';
import { Text, Flex, Icon } from '@upsales/components';
import createAccountAddressPin from './mapPins/AccountAddressPin';

export type AddressMapProps = {
	addresses: Record<string, AccountAddress>;
	labels: Record<string, string>;
	onAddressUpdate?: (type: string, updatedAddress: AccountAddress) => void;
	onAddressClick?: (type: string) => void;
	recentlyUpdatedAddresses?: string[];
};

const AddressMap: React.FC<AddressMapProps> = ({ addresses, onAddressUpdate, recentlyUpdatedAddresses }) => {
	const { t } = useTranslation();
	const [pins, setPins] = useState<Pin<AccountAddress>[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const formatAddressString = (address: AccountAddress): string => {
		const parts = [];
		if (address.address) parts.push(address.address);
		if (address.zipcode && address.city) {
			parts.push(`${address.zipcode} ${address.city}`);
		} else if (address.city) {
			parts.push(address.city);
		}
		if (address.country) parts.push(address.country);
		return parts.join(', ');
	};

	const fetchCoordinatesForAddress = async (
		address: AccountAddress
	): Promise<{ latitude: number; longitude: number } | null> => {
		try {
			// Convert undefined to null for StructuredAddress compatibility
			const accountAddress = {
				address: address.address ?? null,
				city: address.city ?? null,
				zipcode: address.zipcode ?? null,
				country: address.country ?? null
			};

			const { data: result } = await GeocodeResource.lookupAddress(accountAddress, { lookupIfNotFound: true });
			// Use explicit null checks for better coordinate validation
			if (result?.latitude != null && result?.longitude != null) {
				return {
					latitude: result.latitude,
					longitude: result.longitude
				};
			}
			return null;
		} catch (err) {
			console.error('Failed to fetch coordinates for address:', err);
			return null;
		}
	};

	const createPin = (addressType: string, address: AccountAddress): Pin<AccountAddress> | null => {
		// Add addressType to the address data for the pin
		const addressWithType = { ...address, type: addressType };

		// Use the AccountAddressPin component to create the pin
		return createAccountAddressPin(addressWithType);
	};

	const processAddresses = async () => {
		setIsLoading(true);
		setError(null);

		try {
			const newPins: Pin<AccountAddress>[] = [];
			const addressesToUpdate: { type: string; coordinates: { latitude: number; longitude: number } }[] = [];

			// Process addresses in parallel instead of sequentially for better performance
			const addressEntries = Object.entries(addresses);
			const coordinatePromises = addressEntries.map(async ([addressType, address]) => {
				if (!address || !formatAddressString(address)) {
					return null; // Skip empty addresses
				}

				// If coordinates already exist, create pin directly
				if (address.latitude != null && address.longitude != null) {
					const pin = createPin(PIN_TYPE.ACCOUNT_ADDRESS, address);
					if (pin) {
						return { pin, addressType, coordinates: null, updatedAddress: null };
					}
				} else {
					// Fetch coordinates for addresses without them
					const coordinates = await fetchCoordinatesForAddress(address);
					if (coordinates) {
						const updatedAddress = {
							...address,
							...coordinates
						};

						const pin = createPin(PIN_TYPE.ACCOUNT_ADDRESS, updatedAddress);
						if (pin) {
							return { pin, addressType, coordinates, updatedAddress };
						}
					}
				}
				return null;
			});

			const results = await Promise.all(coordinatePromises);

			for (const result of results) {
				if (result) {
					newPins.push(result.pin);
					const wasRecentlyUpdated = recentlyUpdatedAddresses?.includes(result.addressType);

					if (result.coordinates && result.updatedAddress && !wasRecentlyUpdated) {
						// Queue for backend update
						addressesToUpdate.push({
							type: result.addressType,
							coordinates: result.coordinates
						});

						// Notify parent component
						onAddressUpdate?.(result.addressType, result.updatedAddress);
					}
				}
			}

			setPins(newPins);
		} catch (err) {
			console.error('Error processing addresses:', err);
			setError(t('error.failedToLoadMap'));
		} finally {
			setIsLoading(false);
		}
	};

	useEffect(() => {
		processAddresses();
	}, [JSON.stringify(addresses)]);

	if (error) {
		return (
			<Flex direction="column" alignItems="center" justifyContent="center" style={{ minHeight: '300px' }}>
				<Icon name="exclamation-triangle" color="orange" />
				<Text color="orange" space="mtm">
					{error}
				</Text>
			</Flex>
		);
	}

	if (isLoading) {
		return (
			<Flex alignItems="center" justifyContent="center" style={{ minHeight: '300px' }}>
				<Text>{t('default.loading')}</Text>
			</Flex>
		);
	}

	if (pins.length === 0) {
		return (
			<Flex direction="column" alignItems="center" justifyContent="center" style={{ minHeight: '300px' }}>
				<img src="img/up_map_not_found.svg" alt={t('noResult.address')} />
				<Text bold space="mtm">
					{t('noResult.address')}
				</Text>
				<Text size="sm" color="grey-11">
					{t('address.addToGetMap')}
				</Text>
			</Flex>
		);
	}

	return <Map pins={pins} centerMapOnPins showTools allowScrollZoom allowFullScreen hideSearch />;
};

export default AddressMap;
